import { visionLabApi } from '@/app/apiClient';
import { type ApiCallOptions, ApiHttpError } from '@/shared/api/client';
import { z } from 'zod';
import {
  type DuplicateAssetConflict,
  duplicateAssetConflictSchema,
} from '../types/submissionConflicts';
import type {
  LocalSubmissionStatusItem,
  RestartUploadResponseWire,
  SubmissionMediaType,
  SubmissionWire,
  SubmissionsPageWire,
  TransferHistoryQueryBucket,
} from '../types/uploads';

/**
 * Transport for the direct browser->GCS submission channel. Every function uses the shared
 * `visionLabApi` client (relative `/api`, Firebase bearer) with typed wire DTOs. The actual
 * chunk `PUT`s go directly to the GCS resumable URI from the upload engine (outside this transport).
 */

export interface CreateSubmissionBody {
  sessionId: string;
  projectId: string;
  contentBlake3: string;
  mediaType: SubmissionMediaType;
  /** The source file name (backend requires it non-blank; display-only). */
  originalFilename: string;
  declaredBytes: number;
}

const seg = (id: string) => encodeURIComponent(id);

const ownedSubmissionConflictSchema = z
  .object({
    error: z.literal('duplicate_submission'),
    conflict: z
      .object({
        sessionId: z.string().min(1),
        status: z.enum(['pending', 'uploading', 'uploaded', 'failed', 'abandoned']),
      })
      .strict(),
  })
  .strict();

export type SubmissionCreateWireOutcome =
  | { kind: 'created'; submission: SubmissionWire }
  | ({ kind: 'duplicate-asset' } & DuplicateAssetConflict)
  | {
      kind: 'conflict';
      sessionId: string;
      status: 'pending' | 'uploading' | 'uploaded' | 'failed' | 'abandoned';
    };

function ownedSubmissionConflictOutcome(error: unknown): SubmissionCreateWireOutcome | undefined {
  if (
    !(error instanceof ApiHttpError) ||
    error.status !== 409 ||
    error.serverMessage !== 'duplicate_submission'
  ) {
    return undefined;
  }
  const parsed = ownedSubmissionConflictSchema.safeParse(error.payload);
  if (!parsed.success) return undefined;
  return {
    kind: 'conflict',
    sessionId: parsed.data.conflict.sessionId,
    status: parsed.data.conflict.status,
  };
}

function duplicateAssetConflictOutcome(error: unknown): SubmissionCreateWireOutcome | undefined {
  if (
    !(error instanceof ApiHttpError) ||
    error.status !== 409 ||
    error.serverMessage !== 'duplicate_asset'
  ) {
    return undefined;
  }
  const parsed = duplicateAssetConflictSchema.safeParse(error.payload);
  if (!parsed.success) return undefined;
  return { kind: 'duplicate-asset', ...parsed.data };
}

export async function createSubmission(
  body: CreateSubmissionBody,
  options: ApiCallOptions = {},
): Promise<SubmissionCreateWireOutcome> {
  const { declaredBytes, ...rest } = body;
  // Byte counts cross the wire as decimal STRINGS so a multi-GB upload keeps integer precision in JS
  // (the backend parses `declaredBytes`/`cumulativeBytes` from strings); the engine still works in numbers.
  try {
    const response = await visionLabApi.post<SubmissionWire>('/submissions', {
      ...options,
      json: { ...rest, declaredBytes: String(declaredBytes) },
    });
    return { kind: 'created', submission: response };
  } catch (error) {
    const conflict = ownedSubmissionConflictOutcome(error);
    if (conflict) return conflict;
    const duplicateAsset = duplicateAssetConflictOutcome(error);
    if (duplicateAsset) return duplicateAsset;
    throw error;
  }
}

export async function resumableBegin(
  sessionId: string,
  contentType?: string,
  options: ApiCallOptions = {},
): Promise<SubmissionWire> {
  return visionLabApi.post<SubmissionWire>(`/submissions/${seg(sessionId)}/resumable-begin`, {
    ...options,
    json: contentType ? { contentType } : {},
  });
}

export async function reportProgress(
  sessionId: string,
  partNo: number,
  cumulativeBytes: number,
  options: ApiCallOptions = {},
): Promise<SubmissionWire> {
  return visionLabApi.post<SubmissionWire>(`/submissions/${seg(sessionId)}/report-progress`, {
    ...options,
    json: { partNo, cumulativeBytes: String(cumulativeBytes) },
  });
}

export async function completeSubmission(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<SubmissionWire> {
  return visionLabApi.post<SubmissionWire>(`/submissions/${seg(sessionId)}/complete`, {
    ...options,
    json: {},
  });
}

export async function failSubmission(
  sessionId: string,
  reason: string,
  options: ApiCallOptions = {},
): Promise<SubmissionWire> {
  return visionLabApi.post<SubmissionWire>(`/submissions/${seg(sessionId)}/fail`, {
    ...options,
    json: { reason },
  });
}

export async function getSubmission(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<SubmissionWire> {
  return visionLabApi.get<SubmissionWire>(`/submissions/${seg(sessionId)}`, options);
}

/** Batch post-upload lifecycle status for direct-upload sessions. Kept separate from source imports
 * so `up-*` ids never reach the import-only contract. */
export async function fetchLocalSubmissionsStatus(
  sessionIds: string[],
  options: ApiCallOptions = {},
): Promise<{ items: LocalSubmissionStatusItem[] }> {
  return visionLabApi.post<{ items: LocalSubmissionStatusItem[] }>('/submissions/status', {
    ...options,
    json: { sessionIds },
  });
}

/** The GCS resumable committed byte offset (next byte to write) — backend-authoritative resume point. */
export async function getUploadOffset(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<{ offset: string }> {
  return visionLabApi.get<{ offset: string }>(
    `/submissions/${seg(sessionId)}/upload-offset`,
    options,
  );
}

/** The caller-minted replacement ID makes an ambiguous response safe to replay. */
export async function restartExpiredSubmission(
  sessionId: string,
  replacementSessionId: string,
  options: ApiCallOptions = {},
): Promise<RestartUploadResponseWire> {
  return visionLabApi.post<RestartUploadResponseWire>(
    `/submissions/${seg(sessionId)}/restart-expired`,
    { ...options, json: { replacementSessionId } },
  );
}

/** The caller's own submission sessions (keyset-paginated) — the /transfers history source. */
export async function listMySubmissions(
  uid: string,
  {
    limit,
    cursor,
    bucket,
  }: { limit?: number; cursor?: string; bucket?: TransferHistoryQueryBucket } = {},
  options: ApiCallOptions = {},
): Promise<SubmissionsPageWire> {
  return visionLabApi.get<SubmissionsPageWire>(`/submissions/by-uploader/${seg(uid)}`, {
    ...options,
    searchParams: { limit, cursor: cursor || undefined, bucket },
  });
}
